import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { DemandIQDots, DemandIQLogo } from '@demand-iq/ui';
import useHasApp from 'hooks/useHasApp';
import useHasPermission from 'hooks/useHasPermission';
import {
  Activity,
  BookOpen,
  Bot,
  Calendar,
  CalendarDays,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Filter,
  GraduationCap,
  LayoutGrid,
  LayoutTemplate,
  Megaphone,
  MessageSquare,
  Monitor,
  PenLine,
  Plug,
  Search,
  Settings,
  Smartphone,
  UserCog,
  Users,
  UsersRound,
  Video,
} from 'lucide-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import {
  aceAutoFollowUp,
  aceConversations,
  aceSettings,
  aceTeach,
  aceToday,
  calendar,
  canvassingSettings,
  heroInsights,
  heroMembers,
  heroProspects,
  heroTeams,
  integrations,
  knowledgeBase,
  marketingResources,
  miaAdCopywriter,
  miaCanonryDashboard,
  miaGoogleAiSearch,
  miaMetaAds,
  miaSettings,
  miaTeach,
  miaToday,
  miaVideoAds,
  onlineEstimatorSettings,
  presentations,
  presentationsDefaultPath,
  workflowLanding,
} from 'routes/routes';
import { isVideoAdsRetired } from 'utils/mia/videoAdsSunset';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import CompanyNavMenu from './CompanyNavMenu';

const JourneysAppIcon = (): JSX.Element => (
  <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.26287 2.16583C7.58643 2.16583 7.03807 2.7142 7.03807 3.39064C7.03807 4.06708 7.58643 4.61545 8.26287 4.61545C8.93931 4.61545 9.48768 4.06708 9.48768 3.39064C9.48768 2.7142 8.93931 2.16583 8.26287 2.16583ZM12 3.18614C11.3954 3.18614 10.9052 3.67626 10.9052 4.28086V10.2474H6.74825C6.22781 10.2474 5.80591 10.6693 5.80591 11.1898V12.8245C5.80591 13.0846 5.91341 13.3331 6.10296 13.5112L10.3794 17.5295C10.5689 17.7076 10.6765 17.9562 10.6765 18.2163V22.1473C10.6765 22.854 11.4255 23.3092 12.0528 22.9838L12.8152 22.5882C13.1275 22.4261 13.3235 22.1036 13.3235 21.7517V18.2163C13.3235 17.9562 13.431 17.7076 13.6205 17.5295L17.897 13.5112C18.0865 13.3331 18.194 13.0846 18.194 12.8245V11.1898C18.194 10.6693 17.7721 10.2474 17.2517 10.2474H16.8558V7.71265C16.8558 7.10805 16.3657 6.61793 15.7611 6.61793C15.1565 6.61793 14.6664 7.10805 14.6664 7.71265V10.2474H14.6664V9.36108C14.476 10.1989 13.28 10.2001 13.0947 9.3611V4.28086C13.0947 3.67626 12.6046 3.18614 12 3.18614ZM8.26287 5.67177C7.65827 5.67177 7.16815 6.1619 7.16815 6.76649V8.21935C7.16815 8.82395 7.65827 9.31407 8.26287 9.31407C8.86747 9.31407 9.35759 8.82395 9.35759 8.21935V6.76649C9.35759 6.1619 8.86747 5.67177 8.26287 5.67177Z"
      fill="currentColor"
    />
  </svg>
);

type NavSubItem = Readonly<{
  label: string;
  to: string;
  icon: React.ElementType;
}>;

type NavItem = Readonly<{
  label: string;
  to: string;
  icon: React.ElementType;
  avatarSrc?: string;
  external?: boolean;
  description?: string;
  children?: NavSubItem[];
  badge?: string;
  beta?: boolean;
}>;

/** Segment-aware prefix match: '/company' matches '/company/settings' but not '/companies'. */
const matchesPath = (pathname: string, to: string): boolean => pathname === to || pathname.startsWith(`${to}/`);

/**
 * The `to` of the child that should highlight for this pathname — longest match wins, so
 * '/presentations/templates/settings' activates Settings rather than Templates, while deep
 * routes mirrored from the presentations iframe ('/presentations/templates/<deckId>/edit')
 * still activate Templates.
 */
const findActiveChildTo = (children: readonly NavSubItem[] | undefined, pathname: string): string | null =>
  children?.reduce<string | null>(
    (best, child) => (matchesPath(pathname, child.to) && child.to.length > (best?.length ?? -1) ? child.to : best),
    null
  ) ?? null;

type CollapsibleNavItemProps = Readonly<{
  item: NavItem;
  sidebarState: 'expanded' | 'collapsed';
  pathname: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}>;

const CollapsibleNavItem = ({
  item,
  sidebarState,
  pathname,
  isOpen,
  onOpenChange,
}: CollapsibleNavItemProps): JSX.Element => {
  const { t } = useTranslation('common');
  const activeChildTo = findActiveChildTo(item.children, pathname);
  const isChildActive = activeChildTo !== null;
  // The open sub-list already highlights the active child; highlighting the trigger too lights
  // two rows at once (ENG-982). Keep the pill on the trigger only while the sub-list is hidden.
  const isTriggerActive = isChildActive && !isOpen;

  // Items without children render as a plain link (e.g. the Ace coming-soon entry).
  if (!item.children || item.children.length === 0) {
    const isActive = !item.external && matchesPath(pathname, item.to);
    const isCollapsed = sidebarState === 'collapsed';
    return (
      <SidebarMenuButton
        asChild
        isActive={item.avatarSrc ? false : isActive}
        tooltip={item.label}
        size={item.avatarSrc ? 'lg' : 'default'}
        className={cn(
          'p-[var(--spacing-sm)] gap-[var(--spacing-sm)]',
          item.avatarSrc ? 'hover:bg-transparent data-[active=true]:bg-transparent' : ''
        )}
      >
        <NavLink to={item.to}>
          {item.avatarSrc ? (
            <img
              src={item.avatarSrc}
              alt={item.label}
              className={cn(
                'shrink-0 rounded-md object-cover',
                // Match the size of sibling avatar items in each state.
                isCollapsed ? 'size-8' : 'size-6',
                isActive ? 'border border-sidebar-ring' : 'border border-sidebar-border'
              )}
            />
          ) : (
            <item.icon />
          )}
          {/* Label and badge are hidden when collapsed; the tooltip surfaces the label on hover. */}
          {!isCollapsed && <span>{item.label}</span>}
          {!isCollapsed && item.badge && (
            <span className="ml-auto rounded-full border border-sidebar-ring bg-pill-teal px-2 py-0.5 text-[10px] font-semibold leading-none text-pill-foreground">
              {item.badge}
            </span>
          )}
        </NavLink>
      </SidebarMenuButton>
    );
  }

  if (sidebarState === 'collapsed') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            tooltip={item.label}
            isActive={isChildActive}
            size={item.avatarSrc ? 'lg' : 'default'}
            className={cn(
              'p-[var(--spacing-sm)] gap-[var(--spacing-sm)]',
              item.avatarSrc ? 'hover:bg-transparent data-[active=true]:bg-transparent' : ''
            )}
          >
            {item.avatarSrc ? (
              <img
                src={item.avatarSrc}
                alt={item.label}
                className={cn(
                  'size-8 shrink-0 rounded-md object-cover',
                  isChildActive ? 'border border-sidebar-ring' : 'border border-sidebar-border'
                )}
              />
            ) : (
              <item.icon />
            )}
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start">
          <DropdownMenuLabel>{item.label}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {item.children?.map((child) => (
            <DropdownMenuItem key={child.to} asChild>
              <NavLink to={child.to}>{child.label}</NavLink>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange} className="group/collapsible">
      <CollapsibleTrigger asChild>
        <SidebarMenuButton
          isActive={item.avatarSrc ? false : isTriggerActive}
          className={cn(
            'p-[var(--spacing-sm)] gap-[var(--spacing-sm)]',
            item.avatarSrc ? 'hover:bg-transparent data-[state=open]:hover:bg-transparent' : '',
            // Dimming stays keyed off isChildActive so an avatar row doesn't fade while its own
            // section is open and in use.
            item.avatarSrc && !isChildActive ? 'opacity-60 hover:opacity-100' : ''
          )}
        >
          {item.avatarSrc ? (
            <img
              src={item.avatarSrc}
              alt={item.label}
              className={cn(
                'size-6 shrink-0 rounded-md object-cover',
                isTriggerActive ? 'border border-sidebar-ring' : 'border border-sidebar-border'
              )}
            />
          ) : (
            <item.icon />
          )}
          <span>{item.label}</span>
          {item.description && <span className="ml-1 text-xs text-muted-foreground">{item.description}</span>}
          <span className="ml-auto flex items-center gap-1">
            {item.badge && (
              <span className="rounded-full border border-sidebar-ring px-2 py-0.5 text-[10px] font-semibold leading-none text-sidebar-ring">
                {item.badge}
              </span>
            )}
            {item.beta && (
              <span className="rounded-full border border-transparent bg-primary px-2 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground">
                {t('nav-beta-badge', 'BETA AI')}
              </span>
            )}
            <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-data-[state=open]/collapsible:rotate-90" />
          </span>
        </SidebarMenuButton>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <SidebarMenuSub>
          {item.children?.map((child) => {
            const isActive = child.to === activeChildTo;
            return (
              <SidebarMenuSubItem key={child.to}>
                <SidebarMenuSubButton asChild isActive={isActive}>
                  <NavLink to={child.to}>
                    <child.icon />
                    <span>{child.label}</span>
                  </NavLink>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            );
          })}
        </SidebarMenuSub>
      </CollapsibleContent>
    </Collapsible>
  );
};

const AppSidebar = (): JSX.Element => {
  const { t } = useTranslation('common');
  const { state, toggleSidebar } = useSidebar();
  const { pathname } = useLocation();
  const hasPermission = useHasPermission();
  const hasApp = useHasApp();

  const isAdmin = hasPermission({ roles: ['DIQAdmin', 'DIQSupport', 'Admin'] });
  const isRep = hasPermission({ roles: ['Rep'] });
  const { hasApp: hasPresentations } = hasApp('presentations');
  const { hasApp: hasHero } = hasApp('hero');
  const { hasApp: hasPolaris } = hasApp('polaris');
  const aceEnabled = useFeatureFlagEnabled('ace');
  const miaEnabled = useFeatureFlagEnabled('mia');
  // ENG-1127: AI Visibility (Canonry) rolls out behind a flag on top of the admin-role check.
  const canonryEnabled = useFeatureFlagEnabled('canonry');
  // ENG-1047: Video Ads disappears from the nav on its sunset date. See utils/mia/videoAdsSunset.
  const videoAdsRetired = isVideoAdsRetired();

  // YOUR AI CREW
  const miaChildren: NavSubItem[] = [
    ...(miaEnabled
      ? [
          { label: t('nav-mia-today', 'Today'), to: miaToday, icon: CalendarDays },
          { label: t('nav-mia-meta-ads', 'Meta Ads'), to: miaMetaAds, icon: Megaphone },
        ]
      : []),
    ...(videoAdsRetired ? [] : [{ label: t('nav-mia-video-ads', 'Video Ads'), to: miaVideoAds, icon: Video }]),
    { label: t('nav-mia-ad-copywriter', 'Ad Copywriter'), to: miaAdCopywriter, icon: PenLine },
    { label: t('nav-mia-google-ai-search', 'AI Technical Site Audit'), to: miaGoogleAiSearch, icon: Search },
    ...(isAdmin && canonryEnabled
      ? [{ label: t('nav-mia-canonry-dashboard', 'AI Visibility'), to: miaCanonryDashboard, icon: Activity }]
      : []),
    ...(miaEnabled
      ? [
          { label: t('nav-mia-teach', 'Teach Mia'), to: miaTeach, icon: GraduationCap },
          { label: t('nav-mia-settings', 'Settings'), to: miaSettings, icon: Settings },
        ]
      : []),
  ];

  const aiCrewItems: NavItem[] = [
    /*
      ENG-1301: we no longer sell Ace. The `ace` flag is off for everyone, and the flag-off branch
      used to substitute a coming-soon teaser entry -- that teaser is gone, so Ace is now simply
      absent from the nav. App.tsx puts every /ace route behind the same flag. Everything below
      (and pages/ace/*) is kept intact: flipping the flag on restores the full Ace nav in one step.
    */
    ...(aceEnabled
      ? [
          {
            label: t('nav-ace', 'Ace'),
            to: aceToday,
            icon: Bot,
            description: t('nav-ace-desc', 'Converts your leads'),
            children: [
              { label: t('nav-ace-today', 'Today'), to: aceToday, icon: CalendarDays },
              { label: t('nav-ace-conversations', 'Conversations'), to: aceConversations, icon: MessageSquare },
              { label: t('nav-ace-auto-follow-up', 'Auto Follow-up'), to: aceAutoFollowUp, icon: Bot },
              { label: t('nav-ace-teach', 'Teach Ace'), to: aceTeach, icon: GraduationCap },
              { label: t('nav-ace-settings', 'Settings'), to: aceSettings, icon: Settings },
            ],
          } as NavItem,
        ]
      : []),
    {
      label: t('nav-mia', 'Mia'),
      to: miaVideoAds,
      icon: Megaphone,
      avatarSrc: '/mia-avatar.png',
      beta: true,
      children: miaChildren,
    },
  ];

  // TOOLS
  const toolItems: NavItem[] = [
    ...(isRep || !hasPolaris
      ? []
      : [
          {
            label: t('nav-online-estimator', 'Online Estimator'),
            to: workflowLanding,
            icon: Filter,
            children: [
              { label: t('nav-journeys', 'Journeys'), to: workflowLanding, icon: JourneysAppIcon },
              { label: t('nav-online-estimator-settings', 'Settings'), to: onlineEstimatorSettings, icon: Settings },
            ],
          } as NavItem,
        ]),
    ...(hasPresentations
      ? [
          {
            label: t('nav-presentations', 'Presentations'),
            to: presentations,
            icon: Monitor,
            badge: t('nav-presentations-badge', 'BETA AI'),
            children: [
              {
                label: t('nav-presentations-my-presentations', 'My Presentations'),
                to: `${presentations}/${presentationsDefaultPath}`,
                icon: LayoutGrid,
              },
              {
                label: t('nav-presentations-templates', 'Templates'),
                to: `${presentations}/templates`,
                icon: LayoutTemplate,
              },
              {
                label: t('nav-presentations-settings', 'Settings'),
                to: `${presentations}/templates/settings`,
                icon: Settings,
              },
            ],
          } as NavItem,
        ]
      : []),
    ...(hasHero
      ? [
          {
            label: t('nav-canvassing-app', 'Canvassing App'),
            to: heroInsights,
            icon: Smartphone,
            children: [
              { label: t('nav-canvassing-dashboard', 'Dashboard'), to: heroInsights, icon: Activity },
              ...(isAdmin
                ? [{ label: t('nav-canvassing-settings', 'Settings'), to: canvassingSettings, icon: Settings }]
                : []),
            ],
          } as NavItem,
        ]
      : []),
  ];

  // BUSINESS
  const businessItems: NavItem[] = [
    {
      label: t('nav-prospects', 'Leads'),
      to: heroProspects,
      icon: Users,
    },
    ...(isRep
      ? []
      : [
          {
            label: t('nav-calendar', 'Calendar'),
            to: calendar,
            icon: Calendar,
          } as NavItem,
        ]),
    ...(isAdmin
      ? [
          {
            label: t('nav-integrations', 'Integrations'),
            to: integrations,
            icon: Plug,
          },
          {
            label: t('nav-teams', 'Teams'),
            to: heroTeams,
            icon: UsersRound,
          },
          {
            label: t('nav-members', 'Members'),
            to: heroMembers,
            icon: UserCog,
          },
        ]
      : []),
  ];

  // RESOURCES
  const resourceItems: NavItem[] = [
    {
      label: t('nav-marketing-resources', 'Marketing Resources'),
      to: marketingResources,
      icon: Megaphone,
    },
    {
      label: t('nav-knowledge-base', 'Knowledge Base'),
      to: knowledgeBase,
      icon: BookOpen,
      external: true,
    },
  ];

  // A company owning neither polaris nor hero has no Tools items, so drop empty groups rather
  // than render a bare group label.
  const collapsibleGroups: ReadonlyArray<Readonly<{ label: string; items: NavItem[] }>> = [
    { label: t('nav-group-ai-crew', 'Your AI Crew'), items: aiCrewItems },
    { label: t('nav-group-tools', 'Tools'), items: toolItems },
  ].filter((group) => group.items.length > 0);
  const flatGroups: ReadonlyArray<Readonly<{ label: string; items: NavItem[] }>> = [
    { label: t('nav-group-business', 'Business'), items: businessItems },
    { label: t('nav-group-resources', 'Resources'), items: resourceItems },
  ];

  const activeCollapsibleLabel =
    collapsibleGroups.flatMap((g) => g.items).find((item) => findActiveChildTo(item.children, pathname) !== null)
      ?.label ?? null;

  const [openNavItem, setOpenNavItem] = useState<string | null>(activeCollapsibleLabel);

  useEffect(() => {
    if (activeCollapsibleLabel) {
      setOpenNavItem(activeCollapsibleLabel);
    }
  }, [activeCollapsibleLabel]);

  const handleOpenChange = (label: string, open: boolean): void => {
    setOpenNavItem(open ? label : null);
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="flex items-start px-4 py-4">
        {state === 'collapsed' ? (
          <DemandIQDots size={24} className="text-sidebar-foreground" fill="currentColor" />
        ) : (
          <DemandIQLogo width={150} height={22} />
        )}
      </SidebarHeader>
      <SidebarSeparator />
      <SidebarContent>
        {collapsibleGroups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarMenu>
              {group.items.map((item) => (
                <SidebarMenuItem key={item.label}>
                  <CollapsibleNavItem
                    item={item}
                    sidebarState={state}
                    pathname={pathname}
                    isOpen={openNavItem === item.label}
                    onOpenChange={(open) => handleOpenChange(item.label, open)}
                  />
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ))}

        {flatGroups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarMenu>
              {group.items.map((item) => {
                const isActive = !item.external && matchesPath(pathname, item.to);
                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
                      {item.external ? (
                        <a href={item.to} target="_blank" rel="noopener noreferrer">
                          <item.icon />
                          <span>{item.label}</span>
                        </a>
                      ) : (
                        <NavLink to={item.to}>
                          <item.icon />
                          <span>{item.label}</span>
                        </NavLink>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <CompanyNavMenu />
        </SidebarMenu>
        <SidebarSeparator className="-mx-2 !w-[calc(100%+1rem)]" />
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={toggleSidebar} tooltip={t('nav-minimize', 'Minimize sidebar')}>
              {state === 'collapsed' ? <ChevronsRight /> : <ChevronsLeft />}
              <span>{t('nav-minimize', 'Minimize sidebar')}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <div className="px-3 py-2 text-xs text-sidebar-foreground group-data-[collapsible=icon]:hidden">
          <a href="https://www.demand-iq.com/tos" target="_blank" rel="noopener noreferrer" className="hover:underline">
            {t('nav-terms-of-use', 'Terms of Use')}
          </a>
          <p className="mt-0.5 text-sidebar-foreground/50">{t('nav-patent', 'Patent Number 11995708')}</p>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
